/**
 * Gaze Tracking Engine - REQ-ARCH-01, REQ-SIG-01, REQ-PHYS-02
 * 
 * Global component that runs MediaPipe gaze tracking when gaze tracking is active.
 * 
 * ARCHITECTURE (REQ-ARCH-01):
 * - Writes directly to CursorStore (transient state, no React re-renders)
 * - Uses 1€ Filter for signal stabilization (REQ-SIG-01)
 * - Applies PhysicsEngine gravity for cursor magnetism (REQ-PHYS-02)
 * 
 * MINIMUM REQUIREMENTS:
 * - Camera: 30 FPS minimum (most built-in webcams support this)
 * - CPU: 2+ cores recommended, single-core may experience lag
 * - RAM: 2GB+ free
 * - GPU: WebGL 2.0 support recommended (falls back to CPU if unavailable)
 * 
 * For weak devices (Surface, low-end laptops):
 * - Uses frame throttling (max 20 FPS processing) to reduce CPU load
 * - Edge-push calibration prevents cursor from getting stuck in corners
 * - Lower camera resolution (480p) for faster processing
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useInputMethod } from '@/contexts/InputMethodContext';
import { InputMethod } from '@/lib/inputMethods';
import { getUserSettings } from '@/lib/userSettings';
import { CursorStore } from '@/features/cursor/CursorStore';
import { OneEuroFilter2D, mapSmoothnessToFilterParams } from '@/features/cursor/OneEuroFilter';
import { PhysicsEngine } from '@/features/physics/PhysicsEngine';

// Base configuration
const BASE_CONFIG = {
  // Edge push: allows cursor to escape corners by adjusting calibration offset
  EDGE_MARGIN: 20,
  EDGE_PUSH_RATE: 0.02,
  // Performance: max FPS for processing (reduce for weak devices)
  TARGET_FPS: 20,
  // Camera constraints
  CAMERA_WIDTH: 480,
  CAMERA_HEIGHT: 360,
  // Dwell timing
  DWELL_INCREMENT: 4, // Progress increment per 50ms
  DWELL_INTERVAL: 50, // Interval in ms
};

interface CalibrationData {
  coefficients: { x: number[]; y: number[] };
  trackingMode: string;
  timestamp: number;
}

export default function GazeTrackingEngine() {
  const location = useLocation();
  const { 
    inputMethod,
    setDwellProgress,
    isTrackingActive 
  } = useInputMethod();
  
  // Skip if on setup page - setup page manages its own MediaPipe instance
  const isSetupPage = location.pathname.includes('/control/');
  
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const faceLandmarkerRef = useRef<any>(null);
  const animationFrameRef = useRef<number>();
  const calibrationRef = useRef<CalibrationData | null>(null);
  const dwellTimerRef = useRef<NodeJS.Timeout>();
  const currentTargetRef = useRef<HTMLElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const isActiveRef = useRef(false);
  
  // 1€ Filter for signal stabilization (REQ-SIG-01)
  const filterRef = useRef<OneEuroFilter2D | null>(null);
  
  // Edge-push calibration offsets (prevents stuck in corners)
  const xOffsetRef = useRef(0);
  const yOffsetRef = useRef(0);
  
  // Frame throttling for performance
  const lastFrameTimeRef = useRef(0);
  const frameInterval = 1000 / BASE_CONFIG.TARGET_FPS;
  
  // Initialize filter with user settings
  useEffect(() => {
    const smoothing = getUserSettings().gazeTrackingSmoothing || 5;
    const { minCutoff, beta } = mapSmoothnessToFilterParams(smoothing);
    filterRef.current = new OneEuroFilter2D(minCutoff, beta);
  }, []);
  
  // Listen for settings changes and control method changes
  useEffect(() => {
    const handleStorageChange = () => {
      const smoothing = getUserSettings().gazeTrackingSmoothing || 5;
      const { minCutoff, beta } = mapSmoothnessToFilterParams(smoothing);
      filterRef.current?.setParameters(minCutoff, beta);
    };
    
    // Listen for control method change to stop immediately
    const handleControlMethodChange = () => {
      const currentMethod = sessionStorage.getItem('controlMethod') || localStorage.getItem('ayuni_input_method');
      if (currentMethod !== 'gaze_tracking' && currentMethod !== 'webcam') {
        isActiveRef.current = false;
        CursorStore.setActive(false);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('controlMethodChanged', handleControlMethodChange);
    window.addEventListener('inputMethodChanged', handleControlMethodChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('controlMethodChanged', handleControlMethodChange);
      window.removeEventListener('inputMethodChanged', handleControlMethodChange);
    };
  }, []);
  
  // Load calibration from localStorage
  const loadCalibration = useCallback(() => {
    try {
      const stored = localStorage.getItem('gazeTrackingCalibration');
      if (stored) {
        const cal = JSON.parse(stored);
        if (cal.coefficients && cal.trackingMode === 'gaze_tracking') {
          calibrationRef.current = cal;
          xOffsetRef.current = 0;
          yOffsetRef.current = 0;
          return true;
        }
      }
    } catch {
      // Silent fail
    }
    return false;
  }, []);
  
  // Cleanup function
  const cleanup = useCallback(() => {
    isActiveRef.current = false;
    CursorStore.setActive(false);
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = undefined;
    }
    
    if (dwellTimerRef.current) {
      clearInterval(dwellTimerRef.current);
      dwellTimerRef.current = undefined;
    }
    
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      videoRef.current.remove();
      videoRef.current = null;
    }
    
    if (faceLandmarkerRef.current) {
      try {
        faceLandmarkerRef.current.close();
      } catch {
        // Ignore close errors
      }
      faceLandmarkerRef.current = null;
    }
    
    xOffsetRef.current = 0;
    yOffsetRef.current = 0;
    filterRef.current?.reset();
    setIsReady(false);
  }, []);
  
  // Initialize MediaPipe
  const initializeMediaPipe = useCallback(async () => {
    try {
      const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
      
      const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
      
      faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
        filesetResolver,
        getFaceLandmarkerOptions('GPU', false)
      );
      
      return true;
    } catch {
      try {
        const { FaceLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
        const { MEDIAPIPE_WASM_PATH, getFaceLandmarkerOptions } = await import("@/lib/mediapipeConfig");
        
        const filesetResolver = await FilesetResolver.forVisionTasks(MEDIAPIPE_WASM_PATH);
        
        faceLandmarkerRef.current = await FaceLandmarker.createFromOptions(
          filesetResolver,
          getFaceLandmarkerOptions('CPU', false)
        );
        
        return true;
      } catch {
        return false;
      }
    }
  }, []);
  
  // Setup webcam with performance-optimized constraints
  const setupWebcam = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: "user", 
          width: { ideal: BASE_CONFIG.CAMERA_WIDTH, max: 640 }, 
          height: { ideal: BASE_CONFIG.CAMERA_HEIGHT, max: 480 },
          frameRate: { ideal: 30, max: 30 }
        }
      });
      
      const video = document.createElement("video");
      video.srcObject = stream;
      video.style.display = "none";
      document.body.appendChild(video);
      await video.play();
      
      videoRef.current = video;
      return true;
    } catch {
      return false;
    }
  }, []);
  
  // Get iris center from landmarks
  const getIrisCenter = (landmarks: any[]) => {
    const leftIris = landmarks[468];
    const rightIris = landmarks[473];
    
    if (!leftIris || !rightIris) return null;
    
    return {
      x: (leftIris.x + rightIris.x) / 2,
      y: (leftIris.y + rightIris.y) / 2
    };
  };
  
  // Calculate gaze position using calibration with edge-push offset
  const calculateGazePosition = useCallback((irisX: number, irisY: number) => {
    const cal = calibrationRef.current;
    if (!cal?.coefficients) return null;
    
    const { x: xCoef, y: yCoef } = cal.coefficients;
    
    const baseX = xCoef[0] + xCoef[1] * irisX + xOffsetRef.current;
    const baseY = yCoef[0] + yCoef[1] * irisY + yOffsetRef.current;
    
    return { x: baseX, y: baseY };
  }, []);
  
  // Tracking loop with 1€ Filter and PhysicsEngine
  const runTrackingLoop = useCallback(() => {
    if (!isActiveRef.current || !faceLandmarkerRef.current || !videoRef.current || !isTrackingActive) {
      if (isActiveRef.current) {
        animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
      }
      return;
    }
    
    const now = performance.now();
    
    // Frame throttling for weak devices
    if (now - lastFrameTimeRef.current < frameInterval) {
      animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
      return;
    }
    lastFrameTimeRef.current = now;
    
    const results = faceLandmarkerRef.current.detectForVideo(videoRef.current, now);
    
    if (results.faceLandmarks && results.faceLandmarks.length > 0) {
      const landmarks = results.faceLandmarks[0];
      const irisCenter = getIrisCenter(landmarks);
      
      if (irisCenter && calibrationRef.current) {
        const gazePos = calculateGazePosition(irisCenter.x, irisCenter.y);
        
        if (gazePos) {
          let rawX = gazePos.x;
          let rawY = gazePos.y;
          
          // Edge-push calibration - prevents getting stuck in corners
          const { EDGE_MARGIN, EDGE_PUSH_RATE } = BASE_CONFIG;
          
          if (rawX < EDGE_MARGIN) {
            xOffsetRef.current += (EDGE_MARGIN - rawX) * EDGE_PUSH_RATE;
            rawX = EDGE_MARGIN;
          } else if (rawX > window.innerWidth - EDGE_MARGIN) {
            xOffsetRef.current -= (rawX - (window.innerWidth - EDGE_MARGIN)) * EDGE_PUSH_RATE;
            rawX = window.innerWidth - EDGE_MARGIN;
          }
          
          if (rawY < EDGE_MARGIN) {
            yOffsetRef.current += (EDGE_MARGIN - rawY) * EDGE_PUSH_RATE;
            rawY = EDGE_MARGIN;
          } else if (rawY > window.innerHeight - EDGE_MARGIN) {
            yOffsetRef.current -= (rawY - (window.innerHeight - EDGE_MARGIN)) * EDGE_PUSH_RATE;
            rawY = window.innerHeight - EDGE_MARGIN;
          }
          
          // Apply 1€ Filter for signal stabilization (REQ-SIG-01)
          const filtered = filterRef.current?.filter(rawX, rawY, now) ?? { x: rawX, y: rawY };
          
          // Apply physics/magnetism (REQ-PHYS-02)
          const cursorState = CursorStore.getState();
          const physics = PhysicsEngine.applyGravity(
            filtered.x, 
            filtered.y,
            cursorState.velocityX,
            cursorState.velocityY
          );
          
          // Clamp to screen
          const finalX = Math.max(0, Math.min(window.innerWidth, physics.x));
          const finalY = Math.max(0, Math.min(window.innerHeight, physics.y));
          
          // Update CursorStore directly (REQ-ARCH-01 - no React re-renders!)
          CursorStore.setPosition(finalX, finalY);
          
          // Dwell detection using PhysicsEngine (faster than elementsFromPoint)
          const magnetTarget = physics.nearestTarget;
          
          // Fallback to elementsFromPoint if no physics targets registered
          let interactiveElement: HTMLElement | null = magnetTarget?.element ?? null;
          
          if (!interactiveElement) {
            const elementsAtPoint = document.elementsFromPoint(finalX, finalY);
            
            // Check if RestMode overlay is present
            const restModeOverlay = elementsAtPoint.find(el => 
              el.classList.contains('bg-black') && 
              el.classList.contains('fixed') &&
              el.classList.contains('inset-0')
            );
            
            if (restModeOverlay) {
              interactiveElement = elementsAtPoint.find(el => {
                if (el === restModeOverlay) return false;
                const isInteractive = el.tagName === 'BUTTON' || 
                  el.tagName === 'A' || 
                  el.getAttribute('role') === 'button' ||
                  el.classList.contains('clickable');
                const overlayIndex = elementsAtPoint.indexOf(restModeOverlay);
                const elementIndex = elementsAtPoint.indexOf(el);
                return isInteractive && elementIndex < overlayIndex;
              }) as HTMLElement | undefined ?? null;
            } else {
              interactiveElement = elementsAtPoint.find(el => 
                el.tagName === 'BUTTON' || 
                el.tagName === 'A' || 
                el.getAttribute('role') === 'button' ||
                el.classList.contains('clickable')
              ) as HTMLElement | undefined ?? null;
            }
          }
          
          if (interactiveElement) {
            if (currentTargetRef.current !== interactiveElement) {
              // New target - reset and start fresh
              if (dwellTimerRef.current) {
                clearInterval(dwellTimerRef.current);
              }
              CursorStore.setDwellProgress(0);
              setDwellProgress(0);
              currentTargetRef.current = interactiveElement;
              CursorStore.setDwellTarget(interactiveElement);
            }
            
            if (!dwellTimerRef.current) {
              let progress = 0;
              dwellTimerRef.current = setInterval(() => {
                progress += BASE_CONFIG.DWELL_INCREMENT;
                const normalizedProgress = progress / 100;
                CursorStore.setDwellProgress(normalizedProgress);
                setDwellProgress(normalizedProgress);
                
                if (progress >= 100) {
                  clearInterval(dwellTimerRef.current!);
                  dwellTimerRef.current = undefined;
                  currentTargetRef.current?.click();
                  currentTargetRef.current = null;
                  CursorStore.setDwellProgress(0);
                  CursorStore.setDwellTarget(null);
                  setDwellProgress(0);
                }
              }, BASE_CONFIG.DWELL_INTERVAL);
            }
          } else {
            if (dwellTimerRef.current) {
              clearInterval(dwellTimerRef.current);
              dwellTimerRef.current = undefined;
            }
            CursorStore.setDwellProgress(0);
            CursorStore.setDwellTarget(null);
            setDwellProgress(0);
            currentTargetRef.current = null;
          }
        }
      }
    }
    
    if (isActiveRef.current) {
      animationFrameRef.current = requestAnimationFrame(runTrackingLoop);
    }
  }, [isTrackingActive, setDwellProgress, calculateGazePosition, frameInterval]);
  
  // Listen for calibration updates from localStorage
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'gazeTrackingCalibration' && inputMethod === InputMethod.GAZE_TRACKING) {
        loadCalibration();
        CursorStore.reset();
        filterRef.current?.reset();
      }
    };
    
    const handleCalibrationUpdate = () => {
      loadCalibration();
      CursorStore.reset();
      filterRef.current?.reset();
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('gazeCalibrationUpdated', handleCalibrationUpdate);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('gazeCalibrationUpdated', handleCalibrationUpdate);
    };
  }, [inputMethod, loadCalibration]);
  
  // Start/stop tracking based on input method
  useEffect(() => {
    const shouldTrack = inputMethod === InputMethod.GAZE_TRACKING && !isSetupPage;
    
    if (!shouldTrack) {
      cleanup();
      return;
    }
    
    const hasCalibration = loadCalibration();
    
    if (!hasCalibration) {
      return;
    }
    
    // Reset cursor to center on start
    CursorStore.reset();
    filterRef.current?.reset();
    
    // Start tracking
    isActiveRef.current = true;
    CursorStore.setActive(true);
    
    const startTracking = async () => {
      const mpReady = await initializeMediaPipe();
      if (!mpReady || !isActiveRef.current) {
        return;
      }
      
      const webcamReady = await setupWebcam();
      if (!webcamReady || !isActiveRef.current) {
        return;
      }
      
      setIsReady(true);
      runTrackingLoop();
    };
    
    startTracking();
    
    return cleanup;
  }, [inputMethod, isSetupPage, cleanup, initializeMediaPipe, setupWebcam, runTrackingLoop, loadCalibration]);
  
  // This component doesn't render anything visible
  return null;
}
